import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from '@/components/admin/AuthGuard';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminAuthWrapper from '@/components/admin/AdminAuthWrapper';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

// Lazy load page components with retry logic for dynamic import failures
// This handles "Failed to fetch dynamically imported module" errors after deployments
const Home2 = lazyWithRetry(() => import('@/pages/Home2'));

const MembershipPurchase = lazyWithRetry(() => import('@/pages/MembershipPurchase'));
const PurchaseFailure = lazyWithRetry(() => import('@/pages/PurchaseFailure'));
const PurchaseSuccess = lazyWithRetry(() => import('@/pages/PurchaseSuccess'));
const PhotoUpload = lazyWithRetry(() => import('@/pages/PhotoUpload'));
const ProfileUpdate = lazyWithRetry(() => import('@/pages/ProfileUpdate'));

// Lazy load admin pages with retry logic
const AdminLogin = lazyWithRetry(() => import('@/pages/admin/Login'));
const AdminCollections = lazyWithRetry(() => import('@/pages/admin/Collections'));
const AdminAirdrops = lazyWithRetry(() => import('@/pages/admin/Airdrops'));
const AdminPrivileges = lazyWithRetry(() => import('@/pages/admin/Privileges'));
const AdminMembers = lazyWithRetry(() => import('@/pages/admin/Members'));
const AdminBilling = lazyWithRetry(() => import('@/pages/admin/Billing'));
const AdminQrCodes = lazyWithRetry(() => import('@/pages/admin/QRCodes'));
const AdminSettings = lazyWithRetry(() => import('@/pages/admin/Settings'));
const AdminOffline = lazyWithRetry(() => import('@/pages/admin/Offline'));
const PublicOffline = lazyWithRetry(() => import('@/pages/OfflinePublic'));
const AdminSubscription = lazyWithRetry(() => import('@/pages/admin/Subscription'));
const AdminSwitchAccount = lazyWithRetry(() => import('@/pages/admin/SwitchAccount'));
const AdminBrand = lazyWithRetry(() => import('@/pages/admin/Brand'));
const AdminTeam = lazyWithRetry(() => import('@/pages/admin/Team'));
const TermsPage = lazyWithRetry(() => import('@/pages/Terms'));
const PrivacyPage = lazyWithRetry(() => import('@/pages/Privacy'));


const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

// Route configuration
export const createRoutes = () => [
  {
    path: '/',
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <Home2 />
          </LazyWrapper>
        ),
      },
      {
        path: 'purchase',
        element: (
          <LazyWrapper>
            <MembershipPurchase />
          </LazyWrapper>
        ),
      },
      {
        path: 'failure',
        element: (
          <LazyWrapper>
            <PurchaseFailure />
          </LazyWrapper>
        ),
      },
      {
        path: 'photo',
        element: (
          <LazyWrapper>
            <PhotoUpload />
          </LazyWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <LazyWrapper>
            <ProfileUpdate />
          </LazyWrapper>
        ),
      },
      {
          path: 'offline-payment',
          element: (
            <LazyWrapper>
              <PublicOffline />
            </LazyWrapper>
          ),
      },
      {
        path: 'success',
        element: (
          <LazyWrapper>
            <PurchaseSuccess />
          </LazyWrapper>
        ),
      },
      {
        path: 'terms',
        element: (
          <LazyWrapper>
            <TermsPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'privacy',
        element: (
          <LazyWrapper>
            <PrivacyPage />
          </LazyWrapper>
        ),
      },
      // Admin routes - wrapped with AdminAuthWrapper to lazy-load Firebase
      {
        path: 'admin',
        element: <AdminAuthWrapper />,
        children: [
          {
            path: 'login',
            element: (
              <LazyWrapper>
                <AdminLogin />
              </LazyWrapper>
            ),
          },
          {
            path: 'switch-account',
            element: (
              <AuthGuard>
                <LazyWrapper>
                  <AdminSwitchAccount />
                </LazyWrapper>
              </AuthGuard>
            ),
          },
          {
            element: (
              <AuthGuard>
                <AdminLayout />
              </AuthGuard>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="collections" replace />,
              },
              {
                path: 'collections',
                element: (
                  <LazyWrapper>
                    <AdminCollections />
                  </LazyWrapper>
                ),
              },
              {
                path: 'airdrops',
                element: (
                  <LazyWrapper>
                    <AdminAirdrops />
                  </LazyWrapper>
                ),
              },
              {
                path: 'privileges',
                element: (
                  <LazyWrapper>
                    <AdminPrivileges />
                  </LazyWrapper>
                ),
              },
              {
                path: 'members',
                element: (
                  <LazyWrapper>
                    <AdminMembers />
                  </LazyWrapper>
                ),
              },
              
              {
                path: 'billing',
                element: (
                  <LazyWrapper>
                    <AdminBilling />
                  </LazyWrapper>
                ),
              },
              {
                path: 'qrcodes',
                element: (
                  <LazyWrapper>
                    <AdminQrCodes />
                  </LazyWrapper>
                ),
              },
              {
                path: 'brand',
                element: (
                  <LazyWrapper>
                    <AdminBrand />
                  </LazyWrapper>
                ),
              },
              {
                path: 'settings',
                element: (
                  <LazyWrapper>
                    <AdminSettings />
                  </LazyWrapper>
                ),
              },
              {
                path: 'offline',
                element: (
                  <LazyWrapper>
                    <AdminOffline />
                  </LazyWrapper>
                ),
              },
              {
                path: 'subscription',
                element: (
                  <LazyWrapper>
                    <AdminSubscription />
                  </LazyWrapper>
                ),
              },
              {
                path: 'team',
                element: (
                  <LazyWrapper>
                    <AdminTeam />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];
